import React, { useState, useEffect } from 'react';
import { Box, Button, Switch, FormControlLabel } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { db } from '../../firebase';
import { doc, setDoc, getDoc, getDocs, collection } from 'firebase/firestore';
import SettingsIcon from '@mui/icons-material/Settings';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { Divider, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { TextField } from '@mui/material';

const DaySetting = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId, dayId, course, documentId } = location.state || {}; // Added documentId from state
  const [courseTitle, setCourseTitle] = useState(course?.title || "Course");
  const [autoApproval, setAutoApproval] = useState(false);
  const [assignmentRequired, setAssignmentRequired] = useState(false);
  const [manualApproval, setManualApproval] = useState(false);
  const [dayTitle, setDayTitle] = useState(`Day ${dayId || ''}`);
  const [isLoading, setIsLoading] = useState(false);
  const [actualDocumentId, setActualDocumentId] = useState(documentId || `day ${dayId}`);

  // Function to extract numerical day from document ID
  const extractDayNumber = (docId) => {
    const match = docId.match(/day\s*(\d+)/i);
    return match ? parseInt(match[1]) : null;
  };

  // Function to find the actual document ID if not provided
  const findActualDocumentId = async () => {
    if (documentId) {
      setActualDocumentId(documentId);
      return documentId;
    }

    if (!courseId || !dayId) return `day ${dayId}`;

    try {
      const dayCollectionRef = collection(db, 'courses', courseId, 'day');
      const snapshot = await getDocs(dayCollectionRef);
      
      let foundDocId = `day ${dayId}`; 
      
      snapshot.forEach((doc) => {
        const docId = doc.id;
        const dayNumber = extractDayNumber(docId);
        
        if (dayNumber === parseInt(dayId)) {
          foundDocId = docId;
        }
      });

      setActualDocumentId(foundDocId);
      return foundDocId;
    } catch (error) {
      console.error("Error finding document ID:", error);
      return `day ${dayId}`;
    }
  };

  useEffect(() => {
  const fetchSettings = async () => {
    if (!courseId || !dayId || isNaN(dayId)) {
      toast.error('Invalid course or day ID.');
      return;
    }

    setIsLoading(true);
    try {
      // Move the function here
      const findActualDocumentId = async () => {
        // your logic here
      };

      const docId = await findActualDocumentId();

      if (!course?.title) {
        const courseDoc = await getDoc(doc(db, 'courses', courseId));
        if (courseDoc.exists()) {
          setCourseTitle(courseDoc.data().title || "Course");
        }
      }

      const settingsDoc = await getDoc(doc(db, 'courses', courseId, 'day', docId));
      if (settingsDoc.exists()) {
        const data = settingsDoc.data();
        setDayTitle(data.dayTitle || `Day ${dayId}`);
        setAutoApproval(data.autoApproval || false);
        setAssignmentRequired(data.assignmentRequired || false);
        setManualApproval(data.manualApproval || false);
      } else {
        setDayTitle(`Day ${dayId}`);
        setAutoApproval(false);
        setAssignmentRequired(false);
        setManualApproval(false);
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
      toast.error('Failed to load settings: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  fetchSettings();
}, [courseId, dayId, course, documentId]);


  const handleSave = async () => {
    if (!courseId || !dayId || isNaN(dayId)) {
      toast.error('Missing or invalid course/day ID.');
      return;
    }
    
    setIsLoading(true);
    try {
      // Use the actual document ID for saving
      const docRef = doc(db, 'courses', courseId, 'day', actualDocumentId);
      
      await setDoc(docRef, {
        autoApproval,
        assignmentRequired,
        manualApproval: autoApproval ? false : manualApproval,
        dayTitle
      }, { merge: true });
      
      toast.success(`Settings saved for ${actualDocumentId}!`);
      //console.log(`Settings saved to document: ${actualDocumentId} (Day ${dayId})`);
      
      // Navigate back to the previous page
      navigate(-1);
    } catch (error) {
      console.error("Error saving settings:", error);
      toast.error('Failed to save settings: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{
      maxWidth: 540,
      margin: '2.5rem auto',
      p: 4,
      border: '1px solid #e5e7eb',
      borderRadius: 4,
      background: '#fff',
      boxShadow: '0 8px 32px rgba(80, 80, 160, 0.12)',
      minHeight: 520
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
        <SettingsIcon sx={{ color: '#7c3aed', fontSize: 38, mr: 1 }} />
        <Typography variant="h4" sx={{ fontWeight: 800, color: '#7c3aed', letterSpacing: '0.5px', m: 0 }}>
          {courseTitle}
        </Typography>
      </Box>
      <Typography variant="subtitle1" sx={{ textAlign: 'center', fontWeight: 600, color: '#6d28d9', mb: 1, mt: 0.5, letterSpacing: '0.2px' }}>
        Day {dayId} Settings
      </Typography>
      <Typography variant="caption" sx={{ 
        textAlign: 'center', 
        color: '#6b7280', 
        mb: 3, 
        display: 'block',
        fontStyle: 'italic'
      }}>
        Document ID: {actualDocumentId}
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: '#6366f1', mb: 1 }}>
          Day Title
        </Typography>
        <TextField
          fullWidth
          variant="outlined"
          value={dayTitle}
          onChange={e => setDayTitle(e.target.value)}
          placeholder={`Day ${dayId}`}
          sx={{
            '& .MuiOutlinedInput-root': {
              '& fieldset': {
                borderColor: '#e5e7eb',
              },
              '&:hover fieldset': {
                borderColor: '#6366f1',
              },
            },
          }}
        />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#6366f1', mb: 2 }}>
        Approval Settings
      </Typography>
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, mb: 3, background: '#fafaff' }}>
        <FormControlLabel
          control={<Switch checked={autoApproval} onChange={e => {
            setAutoApproval(e.target.checked);
            if (e.target.checked) setManualApproval(false);
            else setManualApproval(true);
          }} color="primary" />}
          label={<span><CheckCircleIcon sx={{ color: autoApproval ? '#22c55e' : '#d1d5db', verticalAlign: 'middle', mr: 0.5 }} />Auto Approval: <strong>{autoApproval ? "Yes" : "No"}</strong></span>}
        />
        {!autoApproval && (
          <FormControlLabel
            control={<Switch checked={manualApproval} onChange={e => setManualApproval(e.target.checked)} color="primary" />}
            label={<span><CancelIcon sx={{ color: manualApproval ? '#f59e42' : '#d1d5db', verticalAlign: 'middle', mr: 0.5 }} />Manual Approval: <strong>{manualApproval ? "Yes" : "No"}</strong></span>}
          />
        )}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#6366f1', mb: 2 }}>
        Assignment Settings
      </Typography>
      <Box sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, mb: 3, background: '#fafaff' }}>
        <FormControlLabel
          control={<Switch checked={assignmentRequired} onChange={e => setAssignmentRequired(e.target.checked)} color="primary" />}
          label={<span><AssignmentTurnedInIcon sx={{ color: assignmentRequired ? '#a78bfa' : '#d1d5db', verticalAlign: 'middle', mr: 0.5 }} />Is Assignment Required? <strong>{assignmentRequired ? "Yes" : "No"}</strong></span>}
        />
      </Box>
      <Divider sx={{ my: 3 }} />
      <Box sx={{ mt: 2, textAlign: 'right' }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={isLoading}
          sx={{
            borderRadius: 2,
            px: 4,
            background: '#a78bfa',
            '&:hover': { background: '#7c3aed' },
            fontWeight: 600,
            fontSize: '1rem',
            boxShadow: '0 2px 8px rgba(124,58,237,0.08)'
          }}
        >
          {isLoading ? 'Saving...' : 'Save Settings'}
        </Button>
      </Box>
      
      {/* Debug info - can be removed in production */}
      {process.env.NODE_ENV === 'development' && (
        <Box sx={{ mt: 2, p: 2, border: '1px dashed #e5e7eb', borderRadius: 1, fontSize: '0.75rem', color: '#6b7280' }}>
          <Typography variant="caption" component="div">Debug Info:</Typography>
          <Typography variant="caption" component="div">Course ID: {courseId}</Typography>
          <Typography variant="caption" component="div">Day ID: {dayId}</Typography>
          <Typography variant="caption" component="div">Document ID: {actualDocumentId}</Typography>
          <Typography variant="caption" component="div">Provided Document ID: {documentId || 'Not provided'}</Typography>
        </Box>
      )}
    </Box>
  );
};

export default DaySetting;